import { useCallback } from 'react';
import ReactCreatableSelect from 'react-select/creatable';
import classnames from 'classnames';
import isNil from 'lodash/isNil';

import '@components/mixins/commonStyles.scss';
import '@components/Print/Print_.scss';
import '../Select_.scss';

import {
    DropdownIndicator,
    MenuList,
    Option,
    SelectProps,
    noOptionsMessage,
    getOptionStyles,
} from '@components/Select';
import { ClearIndicator } from '@components/Select/Select';
import { FieldTitle } from '@components/FieldTitle';
import { NotValidFieldText } from '@components/NotValidFieldText';
import { LeftTitle } from '@components/LeftTitle';
import { translate } from '@chaika/i18n/helper';
import { normalizeYoFilter } from '@utils/normalizeYoString';
import { getMenuPortalProps } from '../getMenuPortalProps';
import { useCreatableSelect } from './hooks/useCreatableSelect';

export const NEW_OPTION_VALUE = 'newOptionValue';

export interface CreatableSelectProps extends SelectProps {}

export function CreatableSelect({
    leftTitle,
    title,
    dataTest,
    value = null,
    name,
    disabled,
    options,
    selectedValue,
    placeholder,
    isSearchable = true,
    isClearable = false,
    hideSelectedOptions = true,
    smallMenuWidth,
    isValid,
    notValidText,
    highlightConditionParams,
    menuPortalTarget,
    onChange,
    ...props
}: CreatableSelectProps) {
    const {
        value: resolvedValue,
        placeholder: resolvedPlaceholder,
        handleChange,
        handleFocus,
        handleBlur,
    } = useCreatableSelect({ options, value, selectedValue, placeholder, title, name, onChange });

    const portalProps = getMenuPortalProps(menuPortalTarget, undefined);
    const withValidStatus = !isNil(isValid);

    const handleCreateOption = (label: string) => {
        const option = { label, value: `${NEW_OPTION_VALUE}_${label}` };

        options.unshift(option);
        onChange(option, name);
    };

    const formatCreateLabel = useCallback(
        (label: string) => (
            <div>
                {translate('add')}: {label}
            </div>
        ),
        [],
    );

    const select = (
        <div
            data-test={dataTest}
            data-testid={dataTest}
            className={classnames([
                'component_select__root',
                'component_select__root_alignedValue',
                'font_body',
                smallMenuWidth && 'component_select__root_smallMenuWidth',
            ])}
        >
            {title && <div className={classnames(['title'])}>{!leftTitle && <FieldTitle title={title} />}</div>}
            <ReactCreatableSelect
                placeholder={resolvedPlaceholder ?? '-'}
                value={resolvedValue}
                options={options}
                hideSelectedOptions={hideSelectedOptions}
                isSearchable={isSearchable}
                isClearable={isClearable}
                isDisabled={disabled}
                noOptionsMessage={noOptionsMessage}
                className={classnames(['component_select__root', 'hide_on_print'])}
                classNamePrefix={'react-select'}
                createOptionPosition={'first'}
                filterOption={normalizeYoFilter}
                components={{ DropdownIndicator, MenuList, Option, ClearIndicator }}
                formatCreateLabel={formatCreateLabel}
                styles={{ option: getOptionStyles(highlightConditionParams) }}
                onCreateOption={handleCreateOption}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                {...props}
                {...portalProps}
            />
            <div className={classnames(['for_print', 'print_label'])}>{resolvedValue?.label ?? ''}</div>
            {withValidStatus && !isValid && notValidText && <NotValidFieldText title={notValidText} />}
        </div>
    );

    return leftTitle && title ? <LeftTitle title={title}>{select}</LeftTitle> : select;
}
